import {
  normalizeCvLanguageProficiency,
  type CvLanguageProficiencyCode,
} from '@tabbio/utils/cv-language-proficiency';

/**
 * The languages editors' proficiency picker, shared by the native and web
 * forms (B12). Each option pairs the canonical code that is STORED with the
 * i18n key (`cv.edit.proficiencyLevels.<key>`) whose translation is only ever
 * DISPLAYED, so the Arabic UI never writes "مهني" as data again.
 */
export const LANGUAGE_PROFICIENCY_OPTIONS = [
  { key: 'elementary', code: 'Basic' },
  { key: 'limited', code: 'Intermediate' },
  { key: 'professional', code: 'Professional' },
  { key: 'fullProfessional', code: 'Fluent' },
  { key: 'native', code: 'Native' },
] as const satisfies readonly {
  key: string;
  code: CvLanguageProficiencyCode;
}[];

export type LanguageProficiencyOption =
  (typeof LANGUAGE_PROFICIENCY_OPTIONS)[number];
export type LanguageProficiencyKey = LanguageProficiencyOption['key'];
export type LanguageProficiencyLabelKey =
  `cv.edit.proficiencyLevels.${LanguageProficiencyKey}`;

/** The picker option for a stored code or any legacy label of it. */
export function findLanguageProficiencyOption(
  value: unknown,
): LanguageProficiencyOption | undefined {
  const code = normalizeCvLanguageProficiency(value);
  return LANGUAGE_PROFICIENCY_OPTIONS.find((option) => option.code === code);
}

/**
 * What the editor shows for a stored level: the translated picker label for
 * the five codes, a CEFR level as itself, otherwise the value as stored.
 */
export function languageProficiencyDisplayLabel(
  value: string,
  translate: (key: LanguageProficiencyLabelKey) => string,
): string {
  const option = findLanguageProficiencyOption(value);
  if (option) return translate(`cv.edit.proficiencyLevels.${option.key}`);
  return normalizeCvLanguageProficiency(value) || value.trim();
}
